import { useMutation, useQuery } from '@apollo/client/react';
import { Grid, TextField } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { type Dayjs } from 'dayjs';
import { titleize } from 'inflection';
import { find, map } from 'lodash';
import { debounce, map as mapFp } from 'lodash/fp';
import React, { useState, useRef, useContext } from 'react';

import ImageSection from '../ImageSection';
import OverwatchModal from '../OverwatchModal';

import CurrentUserContext from '@/Contexts/CurrentUser';
import {
  TaskStatusEnum,
  TaskPriorityEnum,
  CreateTaskDocument,
  GetProjectsDocument,
  GetCompanyEventsDocument,
  GetAllReportsDocument,
  GetAllEmployeesDocument,
  UpdateOnboardingStepDocument,
  OnboardingStepsEnum,
} from '@/graphql/generated/graphql';
import useFileUploader from '@/hooks/useFileUploader';
import { type MinimizedModalsType } from '@/types/globals';
import { ENTITY_TYPE, MAX_ATTACHMENTS_ALLOWED } from '@/utils/constants';
import { arrayLength, updateOnboardingCache } from '@/utils/helperFunctions';
import { displayNotification } from '@/utils/notifications';

const DEFAULT_TASK_STATE = {
  title: '',
  description: '',
  status: TaskStatusEnum.Assigned,
  priority: TaskPriorityEnum.Normal,
  userId: '',
  entityType: '',
  entityId: '',
  dueDate: null as Dayjs | null,
  signedIds: [],
};

const normalDebounce = debounce(500);
const mapNode = mapFp('node');

interface CreateTaskModalProps {
  minimized: boolean;
  handleClose: () => void;
  handleMinimize?: (label: MinimizedModalsType['label']) => void;
}

const CreateTaskModal: React.FC<CreateTaskModalProps> = ({
  minimized = false,
  handleClose,
  handleMinimize = () => {},
}) => {
  const { currentUser, currentCompany, dateTimePreference } = useContext(CurrentUserContext);

  const debounceFunc = useRef(normalDebounce((fn) => fn())).current;
  const [taskData, setTaskData] = useState(DEFAULT_TASK_STATE);

  const [entityId, setEntityId] = useState<string | null>(null);

  const { isUploading, files, handleUploadImage, handleRemoveImage } = useFileUploader();

  const [updateOnboardingStep] = useMutation(UpdateOnboardingStepDocument);
  const [createTask, { loading }] = useMutation(CreateTaskDocument, {
    refetchQueries: ['getTasks'],
    onCompleted: () => {
      handleClose();
      displayNotification({ type: 'success', text: 'Task created successfully!' });

      // update onboarding cache in case the onboarding was left incomplete
      if (!currentCompany?.onboardingSteps?.['taskCreated']) {
        updateOnboardingStep({
          variables: { step: OnboardingStepsEnum.TaskCreated },
          update(cache, { data }) {
            updateOnboardingCache(cache, data?.updateOnboardingStep);
          },
        });
      }
    },
    onError: (error) => {
      displayNotification({ type: 'error', text: error.message });
    },
  });

  const { data: projectsData } = useQuery(GetProjectsDocument, {
    skip: !currentCompany?.id,
    variables: { companyId: currentCompany?.id as string },
  });

  const { data: eventsData } = useQuery(GetCompanyEventsDocument, {
    skip: !currentCompany?.id,
    variables: {
      companyId: currentCompany?.id as string,
    },
  });

  const { data: reportsData } = useQuery(GetAllReportsDocument);

  const { data: employeesData } = useQuery(GetAllEmployeesDocument, {
    skip: !currentUser,
  });

  const handleSubmit = () => {
    const data = {
      ...taskData,
      dueDate: taskData.dueDate ? taskData.dueDate.toISOString() : null,
      entityId: (taskData.entityType ? entityId : null) as string,
      signedIds: map(files, 'signedBlobId').filter((id): id is string => id != null),
    };
    createTask({
      variables: {
        task: data,
      },
    });
  };

  const handleOnChange = (
    key: string,
    value: string | TaskStatusEnum | TaskPriorityEnum | Dayjs | null,
    debounce: boolean = true,
  ) => {
    if (debounce) {
      debounceFunc.cancel();
      debounceFunc(() => {
        setTaskData((prev) => ({
          ...prev,
          [key]: value,
        }));
      });

      return;
    }

    setTaskData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const getEntityOptions = () => {
    if (taskData.entityType === 'Project') {
      return mapNode(projectsData?.getProjects?.edges);
    }
    if (taskData.entityType === 'Report') {
      return mapNode(reportsData?.getCompanyReports?.edges);
    }
    if (taskData.entityType === 'Event') {
      return eventsData?.getCompanyEvents;
    }

    return [];
  };

  return (
    <OverwatchModal
      size="md"
      handleClose={handleClose}
      handleMinimize={() => handleMinimize(taskData.title || 'New Task')}
      title="New task form"
      caption="Fill out the details below to Create a new"
      primaryButtonAction={handleSubmit}
      primaryButtonText={`${loading ? 'Creating...' : 'Create'}`}
      disablePrimary={!taskData.title || (taskData.entityType && !entityId) || loading}
      secondaryButtonAction={handleClose}
      secondaryButtonText="Cancel"
      minimized={minimized}
    >
      <Grid component="form" container spacing={2} noValidate onSubmit={handleSubmit}>
        <Grid size={12}>
          <TextField
            fullWidth
            label="Title"
            defaultValue={taskData.title}
            onChange={(e) => {
              handleOnChange('title', e.target.value, false);
            }}
            error={!taskData.title}
            helperText={!taskData.title ? 'title is required' : ''}
          />
        </Grid>
        <Grid size={12}>
          <TextField
            fullWidth
            label="Description"
            multiline
            rows={4}
            defaultValue=""
            onChange={(e) => {
              handleOnChange('description', e.target.value, false);
            }}
          />
        </Grid>
        <Grid size={12}>
          <Autocomplete
            defaultValue={taskData.status}
            options={Object.keys(TaskStatusEnum).map((type) => TaskStatusEnum[type as keyof typeof TaskStatusEnum])}
            getOptionLabel={(option) => titleize(option)}
            onChange={(_, value) => handleOnChange('status', value, false)}
            disableClearable
            renderInput={(params) => <TextField {...params} label="Task Status" fullWidth />}
          />
        </Grid>
        <Grid size={12}>
          <Autocomplete
            defaultValue={taskData.priority}
            options={Object.keys(TaskPriorityEnum).map(
              (type) => TaskPriorityEnum[type as keyof typeof TaskPriorityEnum],
            )}
            getOptionLabel={(option) => titleize(option)}
            onChange={(_, value) => handleOnChange('priority', value, false)}
            disableClearable
            renderInput={(params) => <TextField {...params} label="Task Priority" fullWidth />}
          />
        </Grid>
        <Grid size={12}>
          <Autocomplete
            options={mapNode(employeesData?.getEmployees?.edges) || []}
            defaultValue={mapNode(employeesData?.getEmployees?.edges).find((emp) => emp.id === taskData.userId) || null}
            getOptionLabel={(option) => titleize(`${option.firstName} ${option.lastName}`)}
            onChange={(_, value) => {
              handleOnChange('userId', value?.id as string, false);
            }}
            renderInput={(params) => <TextField {...params} label="User to assign" fullWidth />}
          />
        </Grid>
        <Grid size={12}>
          <Autocomplete
            options={ENTITY_TYPE}
            defaultValue={find(ENTITY_TYPE, (type) => type === taskData.entityType) || null}
            onChange={(_, value) => {
              handleOnChange('entityType', value, false);
              setEntityId(null);
            }}
            renderInput={(params) => <TextField {...params} label="Entity Type" fullWidth />}
          />
        </Grid>
        {taskData.entityType && (
          <Grid size={12}>
            <Autocomplete
              options={getEntityOptions() || []}
              defaultValue={find(getEntityOptions(), (ent) => ent.id == entityId)}
              getOptionLabel={(option) => option.title}
              onChange={(_, value) => {
                setEntityId(value.id);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={`Select ${taskData.entityType}`}
                  fullWidth
                  error={!entityId}
                  helperText={!entityId ? `Please select a ${taskData.entityType}` : ''}
                />
              )}
            />
          </Grid>
        )}
        <Grid size={12}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Due Date"
              value={taskData.dueDate}
              onChange={(date) => handleOnChange('dueDate', date, false)}
              format={dateTimePreference.date}
              slotProps={{
                textField: {
                  fullWidth: true,
                },
              }}
            />
          </LocalizationProvider>
        </Grid>
        <ImageSection
          attachments={files}
          handleRemoveImage={handleRemoveImage}
          disableAttaching={arrayLength(files) >= MAX_ATTACHMENTS_ALLOWED || isUploading}
          handleUploadImage={(e) => handleUploadImage(e, 'tasks')}
          isUploading={isUploading}
          hasImages={arrayLength(files) > 0}
        />
      </Grid>
    </OverwatchModal>
  );
};

export default CreateTaskModal;
